/**
 * Centralized Price Display Configuration for all Asset Types
 * 
 * This module defines exactly what price information should be displayed
 * for each asset type and listing type combination across the marketplace.
 */

import { Currency, formatCurrency, convertCurrency } from './currencyConverter';

// ============================================================
// TYPES
// ============================================================

export type PriceDisplayType = 
  | 'fixed_price'      // Show exact price: $150,000
  | 'rate_with_unit'   // Show with period: $5,000/mo
  | 'budget_max'       // Show with "Up to": Up to $100,000
  | 'salary_range'     // Show range: $80K - $120K/yr
  | 'request_quote'    // Show: "Request Quote"
  | 'contact_pricing'  // Show: "Contact for Pricing"
  | 'no_price';        // Show nothing / hide price area

export interface PriceDisplayResult {
  displayType: PriceDisplayType;
  formattedPrice: string;
  label?: string;        // e.g., "Budget", "Rate", "Salary"
  showInCard: boolean;   // Whether to show in listing card
}

// ============================================================
// RATE UNIT MAPPING
// ============================================================

const RATE_UNIT_SUFFIXES: Record<string, string> = {
  'per_hour': '/hr',
  'hourly': '/hr',
  'hour': '/hr',
  'per_day': '/day',
  'daily': '/day',
  'day': '/day',
  'per_flight_hour': '/fh',
  'flight_hour': '/fh',
  'per_week': '/wk',
  'weekly': '/wk',
  'week': '/wk',
  'per_month': '/mo',
  'monthly': '/mo',
  'month': '/mo',
  'per_year': '/yr',
  'yearly': '/yr',
  'annual': '/yr',
  'year': '/yr',
  'per_project': '',
  'project': '',
  'fixed': '',
  'per_course': '/course',
  'course': '/course',
};

// ============================================================
// SERVICE PRICE FORMATTER
// ============================================================

export function formatServicePrice(
  pricingModel: string | undefined,
  baseRate: number | undefined | null,
  rateUnit: string | undefined,
  currency: Currency = 'USD'
): PriceDisplayResult {
  // Quote-based pricing - no price shown, user must request
  if (pricingModel === 'quote') {
    return {
      displayType: 'request_quote',
      formattedPrice: 'Request Quote',
      showInCard: true,
    };
  }
  
  // Contact-based pricing - no price shown
  if (pricingModel === 'contact') {
    return {
      displayType: 'contact_pricing',
      formattedPrice: 'Contact for Pricing',
      showInCard: true,
    };
  }
  
  // No rate set - show contact fallback
  if (!baseRate) {
    return {
      displayType: 'contact_pricing',
      formattedPrice: 'Contact for Pricing',
      showInCard: true,
    };
  }
  
  // Get suffix from rate unit
  const suffix = RATE_UNIT_SUFFIXES[rateUnit?.toLowerCase() || ''] || '';
  
  // Format with currency
  const formatted = formatCurrency(baseRate, currency, { suffix });
  
  return {
    displayType: suffix ? 'rate_with_unit' : 'fixed_price',
    formattedPrice: formatted,
    showInCard: true,
  };
}

// ============================================================
// JOB SALARY FORMATTER
// ============================================================

export function formatJobSalary(
  salaryMin: number | undefined | null,
  salaryMax: number | undefined | null,
  salaryType: string | undefined,
  currency: Currency = 'USD'
): PriceDisplayResult {
  // No salary info
  if (!salaryMin && !salaryMax) {
    return {
      displayType: 'no_price',
      formattedPrice: 'Competitive',
      label: 'Salary',
      showInCard: false, // Don't show dash for jobs without salary
    };
  }
  
  // Determine suffix based on salary type
  const typeSuffixes: Record<string, string> = {
    'annual': '/yr',
    'monthly': '/mo',
    'hourly': '/hr',
    'daily': '/day',
    'weekly': '/wk',
  };
  const suffix = typeSuffixes[salaryType?.toLowerCase() || ''] || '/yr';
  
  // Format compact salary display
  const formatCompact = (val: number): string => {
    const converted = currency === 'USD' ? val : convertCurrency(val, currency);
    if (converted >= 1000000) return `${(converted / 1000000).toFixed(1)}M`;
    if (converted >= 1000) return `${Math.round(converted / 1000)}K`;
    return Math.round(converted).toString();
  };
  
  // Get currency symbol
  const symbols: Record<Currency, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: 'CA$',
    AUD: 'A$',
  };
  const symbol = symbols[currency] || '$';
  
  let formatted: string;
  if (salaryMin && salaryMax && salaryMin !== salaryMax) {
    formatted = `${symbol}${formatCompact(salaryMin)} - ${symbol}${formatCompact(salaryMax)}${suffix}`;
  } else if (salaryMax) {
    formatted = `${symbol}${formatCompact(salaryMax)}${suffix}`;
  } else if (salaryMin) {
    formatted = `From ${symbol}${formatCompact(salaryMin)}${suffix}`;
  } else {
    formatted = 'Competitive';
  }
  
  return {
    displayType: 'salary_range',
    formattedPrice: formatted,
    label: 'Salary',
    showInCard: true,
  };
}

// ============================================================
// GENERIC LISTING PRICE HELPER
// ============================================================

export interface ListingPriceInput {
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  listingType: string;
  price?: number | null;
  // Services-specific
  pricingModel?: string;
  baseRate?: number | null;
  rateUnit?: string;
  // Jobs-specific
  salaryMin?: number | null;
  salaryMax?: number | null;
  salaryType?: string;
  // Request-specific
  isBuyRequest?: boolean;
  maxBudget?: number | null;
  // Rental-specific
  rentalRate?: number | null;
  rentalPeriod?: string;
}

/**
 * Universal price display resolver for all asset types
 * Returns formatted price string and display configuration
 */
export function getListingPriceDisplay(
  input: ListingPriceInput,
  currency: Currency = 'USD'
): PriceDisplayResult {
  const { assetType, listingType, isBuyRequest } = input;
  
  // ============ SERVICES ============
  if (assetType === 'services') {
    // Service requests don't show price
    if (listingType === 'service_request' || listingType === 'rfq_request') {
      return {
        displayType: 'no_price',
        formattedPrice: '',
        showInCard: false,
      };
    }
    
    // Service offers - use service-specific formatter
    return formatServicePrice(
      input.pricingModel,
      input.baseRate,
      input.rateUnit,
      currency
    );
  }
  
  // ============ JOBS ============
  if (assetType === 'jobs') {
    // Job profiles/seeking listings don't need prominent price
    if (listingType === 'job_profile' || listingType === 'job_seeking') {
      return formatJobSalary(input.salaryMin, input.salaryMax, input.salaryType, currency);
    }
    
    // Job posts and contracts show salary
    return formatJobSalary(input.salaryMin, input.salaryMax, input.salaryType, currency);
  }
  
  // ============ BUY REQUESTS (all asset types) ============
  if (isBuyRequest || listingType?.includes('buy') || listingType?.includes('request')) {
    const budget = input.maxBudget || input.price;
    
    if (!budget) {
      return {
        displayType: 'no_price',
        formattedPrice: 'Contact for details',
        label: 'Budget',
        showInCard: true,
      };
    }
    
    // Check for rental request with rate suffix
    if (listingType?.includes('rent') || listingType?.includes('lease')) {
      const suffix = RATE_UNIT_SUFFIXES[input.rentalPeriod?.toLowerCase() || 'month'] || '/mo';
      return {
        displayType: 'budget_max',
        formattedPrice: formatCurrency(budget, currency, { suffix }),
        label: 'Max Rate',
        showInCard: true,
      };
    }
    
    return {
      displayType: 'budget_max',
      formattedPrice: formatCurrency(budget, currency),
      label: 'Budget',
      showInCard: true,
    };
  }
  
  // ============ RENTALS (helicopters, engines, parts) ============
  if (listingType?.includes('rent') || listingType?.includes('lease')) {
    const rate = input.rentalRate || input.price;
    if (!rate) {
      return {
        displayType: 'contact_pricing',
        formattedPrice: 'Contact for Rate',
        showInCard: true,
      };
    }
    
    const suffix = RATE_UNIT_SUFFIXES[input.rentalPeriod?.toLowerCase() || 'month'] || '/mo';
    return {
      displayType: 'rate_with_unit',
      formattedPrice: formatCurrency(rate, currency, { suffix }),
      showInCard: true,
    };
  }
  
  // ============ STANDARD SALES ============
  if (!input.price) {
    return {
      displayType: 'contact_pricing',
      formattedPrice: 'Price on Request',
      showInCard: true,
    };
  }
  
  return {
    displayType: 'fixed_price',
    formattedPrice: formatCurrency(input.price, currency),
    showInCard: true,
  };
}

// ============================================================
// SERVICE TURNAROUND TIME FORMATTER (for card display)
// ============================================================

export function formatServiceTurnaround(turnaroundTime: string | undefined): string | null {
  if (!turnaroundTime) return null;
  
  const turnaroundLabels: Record<string, string> = {
    'same_day': 'Same Day',
    'next_day': 'Next Day',
    '24_hours': '24h Turnaround',
    '48_hours': '48h Turnaround',
    '1_week': '1 Week',
    '2_weeks': '2 Weeks',
    '1_month': '1 Month',
    'custom': 'Custom',
  };
  
  return turnaroundLabels[turnaroundTime] || turnaroundTime;
}
